// Pure helpers for the Automation page's reprice-rules manager.
//
// Reprice rules live in `automation_rules` with trigger='reprice' and a
// `conditions` jsonb blob. The repriceAgent edge function
// (supabase/functions/repriceAgent/index.ts) reads exactly these keys with
// these fallbacks:
//
//   drop_pct           default 10  — max % markdown per run (the step cap)
//   min_margin_pct     default 15  — hard floor = cost * (1 + min_margin_pct/100)
//   item_age_days_gte  default 30  — only items in stock at least this long
//   strategy           default 'balanced'
//
// Keeping the defaults here 1:1 with the agent means the UI never shows a
// number the agent wouldn't actually use. Everything below is Deno-free and
// vitest-testable — no React, no supabase.

export type RepriceStrategy = 'quick-sale' | 'balanced' | 'max-value';

/** Defaults mirror `repriceForRule` in supabase/functions/repriceAgent/index.ts. */
export const REPRICE_DEFAULTS = {
  dropPct: 10,
  minMarginPct: 15,
  itemAgeDaysGte: 30,
  strategy: 'balanced' as RepriceStrategy,
} as const;

export interface RepriceParams {
  /** Max % markdown applied per run (the per-run step cap). */
  dropPct: number;
  /** Minimum margin over cost — sets the hard price floor. */
  minMarginPct: number;
  /** Only items in stock at least this many days are touched. */
  itemAgeDaysGte: number;
  strategy: RepriceStrategy;
}

function asFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asStrategy(value: unknown): RepriceStrategy {
  return value === 'quick-sale' || value === 'max-value' || value === 'balanced'
    ? value
    : REPRICE_DEFAULTS.strategy;
}

/**
 * Read the real reprice parameters out of a rule's `conditions` jsonb,
 * falling back to the agent's own defaults for any missing key. `conditions`
 * is `unknown` because it arrives as Supabase's `Json` type.
 */
export function parseRepriceConditions(conditions: unknown): RepriceParams {
  const c = (conditions && typeof conditions === 'object' ? conditions : {}) as Record<
    string,
    unknown
  >;
  return {
    dropPct: asFiniteNumber(c.drop_pct, REPRICE_DEFAULTS.dropPct),
    minMarginPct: asFiniteNumber(c.min_margin_pct, REPRICE_DEFAULTS.minMarginPct),
    itemAgeDaysGte: asFiniteNumber(c.item_age_days_gte, REPRICE_DEFAULTS.itemAgeDaysGte),
    strategy: asStrategy(c.strategy),
  };
}

const STRATEGY_LABELS: Record<RepriceStrategy, string> = {
  'quick-sale': 'Quick sale',
  balanced: 'Balanced',
  'max-value': 'Max value',
};

export function strategyLabel(strategy: RepriceStrategy): string {
  return STRATEGY_LABELS[strategy];
}

/**
 * Plain-English statement of the price floor this rule will never cross.
 * Derived directly from the rule's real `min_margin_pct`, matching the
 * agent's `costFloor = purchase_price * (1 + min_margin_pct/100)`.
 */
export function floorGuardrailText(params: RepriceParams): string {
  return `Never below cost + ${params.minMarginPct}% margin`;
}

/** One-line summary of what the rule does, for the rule row subtitle. */
export function repriceSummaryText(params: RepriceParams): string {
  const ageClause =
    params.itemAgeDaysGte > 0 ? `in stock ${params.itemAgeDaysGte}+ days` : 'of any age';
  return `Reprices items ${ageClause} toward ${strategyLabel(
    params.strategy,
  ).toLowerCase()}, up to ${params.dropPct}% per run`;
}

export interface RepriceLastRun {
  /** ISO timestamp of the most recent repriceAgent action. */
  ranAt: string;
  /** How many items that run repriced (count of agent_actions in the batch). */
  itemsRepriced: number;
}

/**
 * Plain-English last-run line. `null` when the agent has never run for this
 * user. Returns a relative-ish absolute phrasing the component can render
 * without pulling in a date library.
 */
export function lastRunText(lastRun: RepriceLastRun | null): string {
  if (!lastRun) return 'Has not run yet';
  const items =
    lastRun.itemsRepriced === 1 ? '1 item repriced' : `${lastRun.itemsRepriced} items repriced`;
  return `${items}`;
}
